// src/screens/AddProduct.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import api from '../../api';
import { compressImage } from '../../utils/compressImage';
import './AddProduct.css';

import addIcon from '../../assets/add-icon.png';
import closeIcon from '../../assets/close-icon.png';
import infoIcon from '../../assets/info-icon.png';

const DELIVERY_OPTIONS = [
  'PEP (PAXI)',
  'PostNet',
  'The Courier Guy',
  'Aramex',
  'DHL',
  'FedEx',
  'UPS',
  'Fastway',
  'Local Pickup',
  'Meet-up (in person)',
  "Seller's Own Courier",
  'Electronic Delivery',
  'Other'
];

const DEPARTMENTS = [
  { key: 'clothing',     label: 'Clothing & Accessories', emoji: '👕' },
  { key: 'jewelry',      label: 'Jewelry & Watches',      emoji: '💍' },
  { key: 'toys',         label: 'Toys & Games',           emoji: '🧸' },
  { key: 'electronics',  label: 'Electronics & Gadgets',  emoji: '📱' },
  { key: 'home',         label: 'Home & Living',          emoji: '🛋️' },
  { key: 'books',        label: 'Books & Stationery',     emoji: '📚' },
  { key: 'art',          label: 'Art & Collectibles',     emoji: '🎨' },
  { key: 'essentials',   label: 'Health & Essentials',    emoji: '🧴' },
  { key: 'sports',       label: 'Sports & Outdoors',      emoji: '⚽' },
  { key: 'luggage',      label: 'Luggage & Travel',       emoji: '🧳' },
  { key: 'courses',      label: 'Courses & E-Books',      emoji: '🎓' },
  { key: 'gaming',       label: 'Gaming Items',           emoji: '🎮' },
  { key: 'vouchers',     label: 'Subscriptions & Vouchers', emoji: '🎟️' },
  { key: 'nft',          label: 'NFTs (Soon)',            emoji: '🖼️' },
  { key: 'other',        label: 'Other',                  emoji: '➕' },
];

function AddProduct({ user }) {
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [pricingType, setPricingType] = useState('raw');
  const [expectedDeliveryDays, setExpectedDeliveryDays] = useState('');
  const [storeId, setStoreId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showPricingInfo, setShowPricingInfo] = useState(false);

  // NEW: condition + delivery
  const [condition, setCondition] = useState('new'); // 'new' | 'used'
  const [deliveryList, setDeliveryList] = useState([]); // array of strings
  const [deliveryIsOther, setDeliveryIsOther] = useState(false);
  const [deliveryOtherText, setDeliveryOtherText] = useState('');

  // 🆕 Department selection (default to "other" so it's always valid)
  const [departmentKey, setDepartmentKey] = useState('other');

  // Delivery dropdown UI state
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const deliveryRef = useRef(null);

  const fileInputRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const fromMarketplace = location.state?.fromMarketplace;

  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(''), 4000);
  };

  useEffect(() => {
    const handleClickOutside = (ev) => {
      if (deliveryRef.current && !deliveryRef.current.contains(ev.target)) {
        setDeliveryOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await api.get(`/stores/by-user/${user.username}`);
        setStoreId(res.data._id);
      } catch (err) {
        console.error('❌ Failed to fetch store:', err);
      }
    };
    if (user?.username) fetchStore();
  }, [user]);

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setImageFiles((prev) => [...prev, ...newFiles].slice(0, 4));
    e.target.value = '';
  };

  const handleRemoveImage = (indexToRemove) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  // NEW: toggle an option in the delivery list
  const toggleDeliveryOption = (opt) => {
    if (opt === 'Other') {
      // if selecting Other, clear other picks and set the flag
      const newOtherState = !deliveryIsOther;
      setDeliveryIsOther(newOtherState);
      setDeliveryOtherText('');
      if (newOtherState) {
        setDeliveryList([]); // ignore any other picked options
      }
      return;
    }

    // if 'Other' currently selected and user picks a normal option -> turn off Other
    if (deliveryIsOther) {
      setDeliveryIsOther(false);
      setDeliveryOtherText('');
    }

    setDeliveryList((prev) => {
      if (prev.includes(opt)) return prev.filter(o => o !== opt);
      return [...prev, opt];
    });
  };

  const onChangeDelivery_native = (e) => {
    // kept only if some logic still uses native select; otherwise not used
    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
    if (selected.includes('Other')) {
      setDeliveryIsOther(true);
      setDeliveryList([]);
    } else {
      setDeliveryIsOther(false);
      setDeliveryOtherText('');
      setDeliveryList(selected);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!title.trim()) return showError('Please enter a product title.');
    if (title.length > 30) return showError('Product title must not exceed 30 characters.');
    if (!category) return showError('Please select a category.');
    if (!departmentKey) return showError('Please select a department.');
    if (imageFiles.length === 0) return showError('Please upload at least one product image.');
    if (category !== 'barter' && (!price || parseFloat(price) <= 0)) {
      return showError('Please enter a valid price.');
    }
    if (!expectedDeliveryDays) return showError('Please select an estimated delivery time.');
    if (!condition) return showError('Please select the product condition.');

    if (deliveryIsOther) {
      if (!deliveryOtherText.trim()) return showError('Please type your delivery method.');
    } else {
      if (deliveryList.length === 0) {
        return showError('Please select at least one delivery method.');
      }
    }

    if (!user?._id) return showError('Missing user ID. Please log in again.');
    if (!storeId) return showError('Your store could not be found. Try again.');

    if (pricingType === 'dynamic') {
      if (!price || isNaN(price) || parseFloat(price) <= 0) {
        return showError('Please enter a valid USD price for dynamic pricing.');
      }
    }

    setLoading(true);

    try {
      let finalPiPrice = price;

      if (pricingType === 'dynamic') {
        const res = await api.get(`/convert/usd-to-pi?usd=${price}`);
        finalPiPrice = parseFloat(res.data.piAmount).toFixed(2);
      }

      // Map key -> enum label for backend
      const mapKeyToLabel = (key) => {
        const f = DEPARTMENTS.find(d => d.key === key);
        return f ? f.label : 'Other';
      };
      const departmentLabel = mapKeyToLabel(departmentKey);

      const formData = new FormData();
      formData.append('name', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('pricingType', pricingType);
      formData.append('expectedDeliveryDays', parseInt(expectedDeliveryDays));
      formData.append('storeId', storeId);
      formData.append('owner', user._id);
      formData.append('username', user.username);

      // 🆕 Department
      formData.append('department', departmentLabel);

      // NEW fields in payload
      formData.append('condition', condition); // 'new' | 'used'
      if (deliveryIsOther) {
        // send “Other” marker + the custom text
        formData.append('deliveryMethod', 'Other');
        formData.append('customOtherDelivery', deliveryOtherText.trim());
      } else {
        // send array as JSON (backend normalizer accepts this)
        formData.append('deliveryMethod', JSON.stringify(deliveryList));
      }

      if (pricingType === 'raw') formData.append('price', finalPiPrice);
      if (pricingType === 'dynamic') formData.append('usdPrice', parseFloat(price));

      console.log(`🖼️ Compressing ${imageFiles.length} images...`);

      const compressedImages = await Promise.all(
        imageFiles.map(async (file, i) => {
          const compressed = await compressImage(file);
          if (compressed.size > 5 * 1024 * 1024) {
            console.warn(`⚠️ Compressed file [${file.name}] is still too large, using original.`);
            return file;
          }
          console.log(`🧪 [${i}] ${file.name} - Original: ${(file.size / 1024).toFixed(2)}KB → Compressed: ${(compressed.size / 1024).toFixed(2)}KB`);
          return compressed;
        })
      );

      compressedImages.forEach((file) => {
        formData.append('media', file);
      });

      for (const [key, value] of formData.entries()) {
        console.log(`📦 ${key}:`, value);
      }

      await api.post('/products', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      navigate(`/marketplace/store/${user.businessUsername}`, { replace: true });
    } catch (err) {
      console.error('❌ Failed to add product:', err);

      if (err?.response?.status === 413) {
        showError('Upload failed: your images are too large. Try uploading fewer or smaller images.');
      } else {
        showError('Something went wrong while uploading. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (user?.status === 'suspended') {
    return (
      <div className="addproduct-fullscreen">
        <div className="addproduct-header">Add New Product</div>
        <div className="addproduct-suspended-message">
          🚫 Your merchant account has been <strong>suspended</strong>. You cannot add products.
        </div>

        <div className="addproduct-footer">
          <button onClick={() => navigate(`/marketplace/store/${user.businessUsername}`, { replace: true })}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!storeId && user?.username) {
    return null;
  }

  // Render selected delivery labels for button
  const selectedDeliveryLabel = () => {
    if (deliveryIsOther) return 'Other (custom)';
    if (deliveryList.length === 0) return 'Select delivery methods';
    if (deliveryList.length === 1) return deliveryList[0];
    return `${deliveryList.length} selected`;
  };

  return (
    <div className="addproduct-fullscreen">
      <div className="addproduct-header">Add New Product</div>
      {errorMsg && <div className="addproduct-error-box">{errorMsg}</div>}

      <form onSubmit={handleSubmit} className="addproduct-content">
        <label>Product Title</label>

        <input
          type="text"
          className={`addproduct-title-input ${title.length >= 30 ? 'input-limit-reached' : ''}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={30}
          required
        />
        <p className="addproduct-word-counter">{title.length} / 30 characters</p>

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          Pricing Type
          <img
            src={infoIcon}
            alt="Info"
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            onClick={() => setShowPricingInfo(true)}
          />
        </label>

        <select
          value={pricingType}
          onChange={(e) => {
            const selected = e.target.value;
            setPricingType(selected);
            setPrice(''); // Reset price input when switching types
          }}
        >
          <option value="raw">Raw Pi</option>
          <option value="dynamic">Dynamic Pricing</option>
        </select>

        <label>{pricingType === 'dynamic' ? 'Price (in USD)' : 'Price (in Pi)'}</label>
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
        />

        <label>Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} />

        <label>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} required>
          <option value="">Select category</option>
          <option value="barter">Barter</option>
          <option value="physical">Physical Products</option>
          <option value="digital">Digital Products</option>
          <option value="service">Service</option>
        </select>

        {/* 🆕 Department (just under Category) */}
        <label>Department</label>
        <select
          value={departmentKey}
          onChange={(e) => setDepartmentKey(e.target.value)}
          required
        >
          {DEPARTMENTS.map((d) => (
            <option key={d.key} value={d.key}>
              {d.emoji} {d.label}
            </option>
          ))}
        </select>

        {/* NEW: Condition (always visible) */}
        <label>Condition</label>
        <div className="addproduct-pill-group">
          <button
            type="button"
            className={`addproduct-pill ${condition === 'new' ? 'active' : ''}`}
            onClick={() => setCondition('new')}
          >
            New
          </button>
          <button
            type="button"
            className={`addproduct-pill ${condition === 'used' ? 'active' : ''}`}
            onClick={() => setCondition('used')}
          >
            Used
          </button>
        </div>

        <label>Estimated Delivery Time</label>
        <select
          value={expectedDeliveryDays}
          onChange={(e) => setExpectedDeliveryDays(e.target.value)}
          required
        >
          <option value="">Select delivery time</option>
          <option value="3">3 Days</option>
          <option value="7">7 Days</option>
          <option value="14">14 Days</option>
          <option value="21">21 Days</option>
          <option value="30">30 Days</option>
        </select>

        {/* NEW: Delivery Method (custom dropdown multi-select) */}
        <label>Delivery Method</label>

        <div className="delivery-dropdown" ref={deliveryRef}>
          <button
            type="button"
            className="delivery-button"
            onClick={() => setDeliveryOpen((s) => !s)}
            aria-haspopup="listbox"
            aria-expanded={deliveryOpen}
          >
            {selectedDeliveryLabel()}
            <span className="delivery-caret">{deliveryOpen ? '▲' : '▼'}</span>
          </button>

          {deliveryOpen && (
            <div className="delivery-menu" role="listbox" aria-multiselectable="true">
              <div className="delivery-menu-scroll">
                {DELIVERY_OPTIONS.map((opt) => {
                  const checked = deliveryIsOther ? opt === 'Other' : deliveryList.includes(opt);
                  return (
                    <label key={opt} className="delivery-option">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDeliveryOption(opt)}
                      />
                      <span className="delivery-option-label">{opt}</span>
                    </label>
                  );
                })}
              </div>

              <div className="delivery-menu-footer">
                <button
                  type="button"
                  className="delivery-done-btn"
                  onClick={() => setDeliveryOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <small style={{ color: '#666', marginTop: 6, display: 'block' }}>
          Tap options to select multiple. Selecting <strong>Other</strong> will switch to a custom input.
        </small>

        {deliveryIsOther && (
          <input
            className="mt8"
            value={deliveryOtherText}
            onChange={(e) => setDeliveryOtherText(e.target.value)}
            placeholder="Type your delivery method (e.g., Mr D, WhatsApp file, In-person at Wits Library)"
            required
          />
        )}

        <label>Upload Product Images (max 4)</label>
        <div className="addproduct-image-preview-container">
          {imageFiles.map((file, i) => (
            <div key={i} className="addproduct-image-wrapper">
              <img src={URL.createObjectURL(file)} alt={`Preview ${i}`} className="addproduct-preview-image" />
              <img src={closeIcon} alt="Remove" className="addproduct-close-icon" onClick={() => handleRemoveImage(i)} />
            </div>
          ))}
          {imageFiles.length < 4 && (
            <div className="addproduct-addmore-card">
              <label htmlFor="image-upload">
                <img src={addIcon} alt="Add more" className="addproduct-add-icon" />
              </label>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          id="image-upload"
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleImageChange}
        />
      </form>

      <div className="addproduct-footer">
        <button
          onClick={() =>
            navigate(
              fromMarketplace
                ? '/marketplace'
                : `/marketplace/store/${user.businessUsername}`,
              { replace: true }
            )
          }
        >
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Uploading...' : 'Add Product'}
        </button>
      </div>

      {showPricingInfo && (
        <div className="pricing-popup-backdrop" onClick={() => setShowPricingInfo(false)}>
          <div className="pricing-popup-content" onClick={(e) => e.stopPropagation()}>
            <h3>What is Pricing Type?</h3>
            <p><strong>Raw Pi</strong> – Fixed Pi price. Risk of Pi fluctuations.</p>
            <p><strong>Dynamic Pricing</strong> – USD-based pricing auto-converted to Pi.</p>
            <button onClick={() => setShowPricingInfo(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddProduct;
